import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Package, Building2, Edit, Trash2, Image as ImageIcon,
  Video, ExternalLink, ShoppingCart, Tag, Info
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const categoryLabels = {
  efoil: 'E-Foil',
  esurf: 'E-Surf',
  boat: 'Bateau électrique',
  accessory: 'Accessoire'
};

const categoryColors = {
  efoil: 'bg-[#152A35] text-[#3498DB] border-[#3498DB]/20',
  esurf: 'bg-[#142915] text-[#00E01A] border-[#00E01A]/20',
  boat: 'bg-[#2A2010] text-[#F39C12] border-[#F39C12]/20',
  accessory: 'bg-[#2C3338] text-[#8C8F94] border-[#8C8F94]/20'
};

const statusLabels = {
  active: 'Actif',
  discontinued: 'Discontinué',
  on_order: 'Sur commande',
  in_development: 'En développement'
};

const ProductEditForm = ({ product, suppliers, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    supplier_id: product.supplier_id || '',
    reference: product.reference || '',
    name: product.name || '',
    category: product.category || 'esurf',
    purchase_price_ht: product.purchase_price_ht || '',
    sale_price_ttc: product.sale_price_ttc || '',
    specs: product.specs || {},
    photos: product.photos || [],
    videos: product.videos || [],
    status: product.status || 'active'
  });
  const [newPhoto, setNewPhoto] = useState('');
  const [newVideo, setNewVideo] = useState('');

  const handleAddPhoto = () => {
    if (newPhoto && !formData.photos.includes(newPhoto)) {
      setFormData({ ...formData, photos: [...formData.photos, newPhoto] });
      setNewPhoto('');
    }
  };

  const handleRemovePhoto = (url) => {
    setFormData({ ...formData, photos: formData.photos.filter(p => p !== url) });
  };

  const handleAddVideo = () => {
    if (newVideo && !formData.videos.includes(newVideo)) {
      setFormData({ ...formData, videos: [...formData.videos, newVideo] });
      setNewVideo('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      purchase_price_ht: formData.purchase_price_ht ? parseFloat(formData.purchase_price_ht) : null,
      sale_price_ttc: formData.sale_price_ttc ? parseFloat(formData.sale_price_ttc) : null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Fournisseur *</Label>
          <Select
            value={formData.supplier_id}
            onValueChange={(value) => setFormData({ ...formData, supplier_id: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Sélectionner..." />
            </SelectTrigger>
            <SelectContent>
              {suppliers.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Catégorie *</Label>
          <Select
            value={formData.category}
            onValueChange={(value) => setFormData({ ...formData, category: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="efoil">E-Foil</SelectItem>
              <SelectItem value="esurf">E-Surf</SelectItem>
              <SelectItem value="boat">Bateau électrique</SelectItem>
              <SelectItem value="accessory">Accessoire</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Référence *</Label>
          <Input
            value={formData.reference}
            onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Nom du produit *</Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>
        <div className="space-y-2">
          <Label>Prix achat HT (€)</Label>
          <Input
            type="number"
            value={formData.purchase_price_ht}
            onChange={(e) => setFormData({ ...formData, purchase_price_ht: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Prix vente TTC (€)</Label>
          <Input
            type="number"
            value={formData.sale_price_ttc}
            onChange={(e) => setFormData({ ...formData, sale_price_ttc: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label>Statut</Label>
          <Select
            value={formData.status}
            onValueChange={(value) => setFormData({ ...formData, status: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Actif</SelectItem>
              <SelectItem value="discontinued">Discontinué</SelectItem>
              <SelectItem value="on_order">Sur commande</SelectItem>
              <SelectItem value="in_development">En développement</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Photos */}
      <div className="space-y-2">
        <Label>Photos</Label>
        <div className="flex gap-2">
          <Input
            value={newPhoto}
            onChange={(e) => setNewPhoto(e.target.value)}
            placeholder="URL de l'image..."
            className="flex-1"
          />
          <Button type="button" variant="outline" onClick={handleAddPhoto}>
            Ajouter
          </Button>
        </div>
        {formData.photos.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.photos.map((url, idx) => (
              <div key={idx} className="relative group">
                <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                <button
                  type="button"
                  onClick={() => handleRemovePhoto(url)}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-[#E74C3C] rounded-full text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Videos */}
      <div className="space-y-2">
        <Label>Vidéos (URLs YouTube/Vimeo)</Label>
        <div className="flex gap-2">
          <Input
            value={newVideo}
            onChange={(e) => setNewVideo(e.target.value)}
            placeholder="URL de la vidéo..."
            className="flex-1"
          />
          <Button type="button" variant="outline" onClick={handleAddVideo}>
            Ajouter
          </Button>
        </div>
        {formData.videos.length > 0 && (
          <div className="space-y-1 mt-2">
            {formData.videos.map((url, idx) => (
              <div key={idx} className="flex items-center justify-between bg-[#141617] rounded p-2 text-sm">
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-[#3498DB] truncate flex-1">
                  {url}
                </a>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, videos: formData.videos.filter((_, i) => i !== idx) })}
                  className="text-[#E74C3C] ml-2"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-[#2C3338]">
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" className="bg-[#00E01A] hover:bg-[#00B815] text-black">
          Enregistrer
        </Button>
      </div>
    </form>
  );
};

const ProductDetail = () => {
  const { api, isSuperAdmin, canSeePrices } = useAuth();
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  const fetchData = async () => {
    try {
      const [productRes, suppliersRes] = await Promise.all([
        api.get(`/products/${productId}`),
        api.get('/suppliers')
      ]);
      setProduct(productRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Erreur lors du chargement du produit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api, productId]);

  const handleUpdate = async (data) => {
    try {
      await api.put(`/products/${productId}`, data);
      toast.success('Produit mis à jour');
      fetchData();
      setEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update product:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/products/${productId}`);
      toast.success('Produit supprimé');
      navigate('/products');
    } catch (error) {
      console.error('Failed to delete product:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-[#2C3338] rounded mb-4"></div>
          <div className="bg-[#1D2327] rounded-xl p-6">
            <div className="h-64 bg-[#2C3338] rounded mb-4"></div>
            <div className="h-6 w-32 bg-[#2C3338] rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6 text-center">
        <Package size={48} className="mx-auto text-[#8C8F94] mb-4" />
        <p className="text-[#8C8F94]">Produit non trouvé</p>
        <Link to="/products" className="text-[#00E01A] hover:underline mt-2 inline-block">
          Retour au catalogue
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/products"
            className="p-2 rounded-lg bg-[#1D2327] border border-[#2C3338] text-[#8C8F94] hover:text-white hover:border-[#00E01A]/30 transition-colors"
            data-testid="back-to-products-btn"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <p className="text-[#8C8F94] text-sm font-mono">{product.reference}</p>
            <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">
              {product.name}
            </h1>
          </div>
        </div>
        
        {isSuperAdmin() && (
          <div className="flex items-center gap-2">
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="edit-product-btn">
                  <Edit size={16} className="mr-2" />
                  Modifier
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#1D2327] border-[#2C3338] text-white max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold font-['Outfit']">
                    Modifier le produit
                  </DialogTitle>
                </DialogHeader>
                <ProductEditForm
                  product={product}
                  suppliers={suppliers}
                  onSave={handleUpdate}
                  onClose={() => setEditDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>

            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-[#E74C3C] border-[#E74C3C]/30 hover:bg-[#E74C3C]/10">
                  <Trash2 size={16} className="mr-2" />
                  Supprimer
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#1D2327] border-[#2C3338]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white">Supprimer ce produit ?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[#8C8F94]">
                    Cette action est irréversible. Le produit sera définitivement supprimé du catalogue.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-[#2C3338] border-none text-white hover:bg-[#3C4348]">
                    Annuler
                  </AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleDelete}
                    className="bg-[#E74C3C] hover:bg-[#C0392B] text-white"
                  >
                    Supprimer
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Images */}
        <div className="space-y-4">
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] overflow-hidden">
            <div className="aspect-video bg-[#141617] relative">
              {product.photos && product.photos.length > 0 ? (
                <img
                  src={product.photos[selectedImage]}
                  alt={product.name}
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Package size={64} className="text-[#2C3338]" />
                </div>
              )}
            </div>
            
            {product.photos && product.photos.length > 1 && (
              <div className="p-3 flex gap-2 overflow-x-auto">
                {product.photos.map((photo, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-colors ${
                      selectedImage === idx ? 'border-[#00E01A]' : 'border-transparent'
                    }`}
                  >
                    <img src={photo} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Videos */}
          {product.videos && product.videos.length > 0 && (
            <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-4">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Video size={18} />
                Vidéos ({product.videos.length})
              </h3>
              <div className="space-y-2">
                {product.videos.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#3498DB] hover:text-[#00E01A] transition-colors text-sm"
                  >
                    <ExternalLink size={14} />
                    <span className="truncate">{url}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="space-y-4">
          {/* Main Info */}
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${categoryColors[product.category]}`}>
                {categoryLabels[product.category]}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                product.status === 'active' 
                  ? 'bg-[#142915] text-[#00E01A]' 
                  : 'bg-[#2C3338] text-[#8C8F94]'
              }`}>
                {statusLabels[product.status]}
              </span>
            </div>

            <div className="flex items-center gap-2 text-[#8C8F94] mb-4">
              <Building2 size={16} />
              <Link 
                to={`/suppliers/${product.supplier_id}`}
                className="hover:text-[#00E01A] transition-colors"
              >
                {product.supplier_name}
              </Link>
            </div>

            {canSeePrices() && (
              <div className="grid grid-cols-3 gap-4 pt-4 border-t border-[#2C3338]">
                <div>
                  <p className="text-[#8C8F94] text-xs mb-1">Prix achat HT</p>
                  <p className="text-white text-xl font-bold">
                    {product.purchase_price_ht 
                      ? `${product.purchase_price_ht.toLocaleString()}€` 
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-[#8C8F94] text-xs mb-1">Prix vente TTC</p>
                  <p className="text-[#00E01A] text-xl font-bold">
                    {product.sale_price_ttc 
                      ? `${product.sale_price_ttc.toLocaleString()}€` 
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-[#8C8F94] text-xs mb-1">Marge</p>
                  <p className="text-white text-xl font-bold">
                    {product.margin_percent 
                      ? `${product.margin_percent.toFixed(1)}%` 
                      : '-'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Specifications */}
          {product.specs && Object.keys(product.specs).length > 0 && (
            <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Info size={18} />
                Spécifications
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(product.specs).map(([key, value]) => (
                  <div key={key} className="bg-[#141617] rounded-lg p-3">
                    <p className="text-[#8C8F94] text-xs capitalize">{key}</p>
                    <p className="text-white font-medium">{value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-[#1D2327] rounded-xl border border-[#2C3338] p-5">
            <h3 className="text-white font-semibold mb-4">Actions rapides</h3>
            <div className="space-y-2">
              <Link
                to={`/inventory?product_id=${product.id}`}
                className="flex items-center gap-3 p-3 bg-[#141617] rounded-lg hover:bg-[#2C3338] transition-colors"
              >
                <Package size={18} className="text-[#00E01A]" />
                <span className="text-white">Voir les équipements vendus</span>
              </Link>
              <Link
                to={`/sav?product_id=${product.id}`}
                className="flex items-center gap-3 p-3 bg-[#141617] rounded-lg hover:bg-[#2C3338] transition-colors"
              >
                <Tag size={18} className="text-[#F39C12]" />
                <span className="text-white">Tickets SAV liés</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
